import { computed, ref } from "vue";
import { selectWordRange } from "@/common/helpers/excerpt-word-range";
// Gates saving, not excerpting: dropped when SAVE_EXCERPTS is withheld or translations are on.
const SAVE_CONTROL_ID = "rc-excerpt-save";
const MENU_ITEM_SELECTOR = "a:not(.rc-disabled)";
const menuElement = () => {
    const id = window.Ramius?.highlight?.confirmId;
    return id ? document.getElementById(id) : null;
};
const menuItems = () => {
    const menu = menuElement();
    return menu ? Array.from(menu.querySelectorAll(MENU_ITEM_SELECTOR)) : [];
};
// The engine binds mouseup/mousedown only and arms its `S` shortcut from inside that mouseup path, so excerpting is pointer-only (WCAG 2.1.1, failure F54); Shift+Arrow grows a chunk range here and drives the engine's own confirm menu.
export const useRamiusExcerptKeyboard = (options) => {
    const anchor = ref(null);
    const focusWordIndex = ref(null);
    const isActive = computed(() => anchor.value !== null && focusWordIndex.value !== null);
    const anchorWordIndex = computed(() => (isActive.value ? anchor.value.wordIndex : null));
    // Lets the owning message bind the menu handler, so sibling messages leave the shared menu alone.
    const activeSegmentId = computed(() => (isActive.value ? anchor.value.segmentId : null));
    const selectedWordCount = computed(() => anchor.value && focusWordIndex.value !== null ? Math.abs(focusWordIndex.value - anchor.value.wordIndex) + 1 : 0);
    // Tab leaves the menu relative to the range, not the anchor, so it needs the ordered edges.
    const selectionBounds = computed(() => anchor.value && focusWordIndex.value !== null
        ? {
            first: Math.min(anchor.value.wordIndex, focusWordIndex.value),
            last: Math.max(anchor.value.wordIndex, focusWordIndex.value)
        }
        : null);
    // validateCapture reads coordinates off the event to place the menu, so the selection's own box stands in for a pointer.
    const showMenuAtSelection = () => {
        const menu = menuElement();
        const selection = window.getSelection();
        if (!menu || !selection || selection.rangeCount === 0)
            return false;
        const rect = selection.getRangeAt(0).getBoundingClientRect();
        // It early-returns while already open, so closing first is what makes a re-open reposition.
        menu.style.display = "none";
        window.Ramius?.highlight?.validateCapture?.({
            target: menu,
            pageX: rect.left + window.scrollX,
            pageY: rect.bottom + window.scrollY,
            clientX: rect.left,
            clientY: rect.bottom,
            keyCode: 0
        });
        return menu.style.display === "block";
    };
    const closeMenu = () => {
        const menu = menuElement();
        if (menu)
            menu.style.display = "none";
    };
    const isMenuOpen = () => menuElement()?.style.display === "block";
    // Parity with the mouse path, which needs only the menu.
    const isAvailable = () => menuElement() !== null;
    const focusFirstMenuItem = () => {
        menuItems()[0]?.focus();
    };
    const moveMenuFocus = (delta) => {
        const items = menuItems();
        if (items.length === 0)
            return false;
        const current = items.indexOf(document.activeElement);
        const next = (current + delta + items.length) % items.length;
        items[next]?.focus();
        return true;
    };
    const clear = () => {
        anchor.value = null;
        focusWordIndex.value = null;
        closeMenu();
    };
    // Anchors on the word the extension started from, so repeated Shift+Arrow grows and shrinks around it.
    const extendBy = (segmentId, anchorWordIndex, delta, wordCount) => {
        // Arrows clamp to their segment and the engine rejects cross-area selections, so a different segment restarts.
        const reanchored = anchor.value?.segmentId !== segmentId;
        const current = reanchored ? { segmentId, wordIndex: anchorWordIndex } : anchor.value;
        const previousFocus = reanchored ? current.wordIndex : focusWordIndex.value ?? current.wordIndex;
        // A one-chunk segment has nothing to extend onto, so select it rather than leaving it unreachable.
        const nextFocus = wordCount === 1 ? previousFocus : previousFocus + delta;
        if (nextFocus < 0 || nextFocus >= wordCount)
            return null;
        const anchorEl = options.wordElementAt(segmentId, current.wordIndex);
        const focusEl = options.wordElementAt(segmentId, nextFocus);
        if (!anchorEl || !focusEl || !selectWordRange(anchorEl, focusEl))
            return null;
        const wasOpen = isMenuOpen();
        anchor.value = current;
        focusWordIndex.value = nextFocus;
        // Focus enters the menu once; later extensions only move it so the user can keep growing the range.
        if (showMenuAtSelection() && !wasOpen)
            focusFirstMenuItem();
        // Growing adds the chunk just reached; shrinking drops the one just left.
        const grew = Math.abs(nextFocus - current.wordIndex) >= Math.abs(previousFocus - current.wordIndex);
        return { changedWordIndex: grew ? nextFocus : previousFocus, grew, wordCount: selectedWordCount.value };
    };
    // The save control has no click or key handler of its own; only the engine's mouseup path reaches its commit branch.
    const commit = () => {
        const save = document.getElementById(SAVE_CONTROL_ID);
        if (!isActive.value || !save)
            return false;
        window.Ramius?.highlight?.validateCapture?.({ target: save, pageX: 0, pageY: 0, keyCode: 0 });
        const committed = !isMenuOpen();
        if (committed) {
            anchor.value = null;
            focusWordIndex.value = null;
            window.getSelection()?.removeAllRanges();
        }
        return committed;
    };
    const isSaveControlFocused = () => document.activeElement?.id === SAVE_CONTROL_ID;
    return {
        isAvailable,
        isActive,
        anchorWordIndex,
        activeSegmentId,
        selectedWordCount,
        selectionBounds,
        extendBy,
        clear,
        commit,
        moveMenuFocus,
        isSaveControlFocused
    };
};
